"use client";

import { useEffect, useRef, useState } from "react";

interface PressReleaseHeaderProps {
  children: React.ReactNode;
  imageWidth?: string;
}

const PressReleaseHeader = ({
  children,
  imageWidth = "max-w-5xl",
}: PressReleaseHeaderProps) => {
  const [opacity, setOpacity] = useState(1);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;

      const headerRect = headerRef.current.getBoundingClientRect();
      const headerBottom = headerRect.bottom;

      // Find the bottom section (the content section that should scroll over the header)
      // It should be the next sibling or a section with z-20 class
      const article = headerRef.current.closest("article");
      if (!article) return;

      // Find the bottom section - it should be a div with z-20 class
      const bottomSection = article.querySelector(
        'div[class*="z-20"]'
      ) as HTMLElement;
      if (!bottomSection) return;

      const bottomSectionRect = bottomSection.getBoundingClientRect();
      const bottomSectionTop = bottomSectionRect.top;

      // Start fading immediately when bottom section's top reaches header's bottom
      // This means the bottom section is starting to overlap the header
      // Add a threshold to ensure we only fade when there's actual visual overlap
      const overlapThreshold = 20; // Only start fading when there's at least 20px of overlap

      // Check if bottom section has actually overlapped the header
      if (bottomSectionTop < headerBottom - overlapThreshold) {
        // Calculate how much overlap has occurred (subtract threshold to start from 0)
        const overlap = headerBottom - bottomSectionTop - overlapThreshold;
        const fadeDistance = 400; // Complete fade over 200px of overlap
        const scrollProgress = Math.min(Math.max(0, overlap) / fadeDistance, 1);
        // Fade to completely hidden (0 opacity)
        setOpacity(Math.max(0, 1 - scrollProgress));
      } else {
        // Bottom section hasn't reached the header yet or overlap is too small
        setOpacity(1);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div
      ref={headerRef}
      className={`sticky top-[60px] z-10 mx-auto ${imageWidth} px-4 transition-all duration-300 ease-out sm:px-6 lg:px-8`}
      style={{
        opacity,
        pointerEvents: opacity <= 0.01 ? "none" : "auto",
        visibility: opacity <= 0.01 ? "hidden" : "visible",
      }}
    >
      <div className="py-8">{children}</div>
    </div>
  );
};

export default PressReleaseHeader;
